import { useEffect, useSyncExternalStore } from 'react';
import type { BoardSnapshot, ClientMessage, Notification, PresenceEntry, ServerMessage } from '@kaba/shared';
import { applyEvent, boardKey, onSnapshotLoaded, queryClient } from './board';
import { toast } from './toast';
import { refreshStructure } from './structure';

type Status = 'connecting' | 'online' | 'offline';

/** 0.48: Beschreibung gemeinsam bearbeiten – eine Sitzung je Karte (lib/collab.ts) */
export interface CollabChannel {
  message: (m: Extract<ServerMessage, { cardId: string }>) => void;
  open: () => void;
  close: () => void;
}

/**
 * Eine WebSocket-Verbindung pro angemeldeter Person. Boards werden
 * an- und abgemeldet; nach einem Abbruch wird mit wachsendem Abstand neu
 * verbunden und ab der letzten Ereignis-ID jedes Boards nachgeladen.
 */
class Realtime {
  private ws: WebSocket | null = null;
  private boards = new Map<string, number>(); // boardId → Anzahl Abonnenten
  private attempt = 0;
  private timer: ReturnType<typeof setTimeout> | undefined;
  private ping: ReturnType<typeof setInterval> | undefined;
  private active = false;
  status: Status = 'connecting';
  private listeners = new Set<() => void>();
  /** Präsenz je Board (vom Server) und meine offene Karte je Board */
  private presence = new Map<string, PresenceEntry[]>();
  private presenceListeners = new Set<() => void>();
  private viewing = new Map<string, string | null>();
  /** mehrere je Karte möglich (eine schließende Sitzung wartet noch aufs Speichern, eine neue beginnt) */
  private collab = new Map<string, Set<CollabChannel>>();

  constructor() {
    onSnapshotLoaded((boardId) => {
      if (this.boards.has(boardId)) this.sendSubscribe(boardId);
    });
  }

  start() {
    if (this.active) return;
    this.active = true;
    this.connect();
    document.addEventListener('visibilitychange', this.wake);
    window.addEventListener('online', this.wake);
  }

  stop() {
    this.active = false;
    clearTimeout(this.timer);
    clearInterval(this.ping);
    document.removeEventListener('visibilitychange', this.wake);
    window.removeEventListener('online', this.wake);
    this.ws?.close();
    this.ws = null;
  }

  subscribe(boardId: string) {
    this.boards.set(boardId, (this.boards.get(boardId) ?? 0) + 1);
    this.sendSubscribe(boardId);
    return () => {
      const n = (this.boards.get(boardId) ?? 1) - 1;
      if (n <= 0) {
        this.boards.delete(boardId);
        this.viewing.delete(boardId);
        this.presence.delete(boardId);
        this.presenceListeners.forEach((l) => l());
        this.send({ type: 'unsubscribe', boardId });
      } else this.boards.set(boardId, n);
    };
  }

  /** Übersicht offen: Kartenänderungen in zugänglichen Boards melden lassen (Zähler für mehrere Ansichten) */
  private cardWatchers = 0;
  watchCards(): () => void {
    if (this.cardWatchers++ === 0) this.send({ type: 'watch-cards', on: true });
    return () => {
      if (--this.cardWatchers === 0) this.send({ type: 'watch-cards', on: false });
    };
  }

  /** Kanal für das gemeinsame Bearbeiten einer Karte; `open` kommt sofort, wenn verbunden */
  attachCollab(cardId: string, ch: CollabChannel): () => void {
    const set = this.collab.get(cardId) ?? new Set<CollabChannel>();
    set.add(ch);
    this.collab.set(cardId, set);
    if (this.ws?.readyState === WebSocket.OPEN) ch.open();
    return () => {
      set.delete(ch);
      if (!set.size && this.collab.get(cardId) === set) this.collab.delete(cardId);
    };
  }

  /** Wie viele Sitzungen hängen an dieser Karte (gleiche Verbindung = ein Platz im Raum) */
  collabCount(cardId: string): number {
    return this.collab.get(cardId)?.size ?? 0;
  }

  /** false = gerade keine Verbindung */
  sendCollab(msg: ClientMessage): boolean {
    if (this.ws?.readyState !== WebSocket.OPEN) return false;
    this.ws.send(JSON.stringify(msg));
    return true;
  }

  isOpen() {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  setViewing(boardId: string, cardId: string | null) {
    this.viewing.set(boardId, cardId);
    this.send({ type: 'presence', boardId, cardId });
  }

  getPresence(boardId: string): PresenceEntry[] {
    return this.presence.get(boardId) ?? EMPTY;
  }

  onPresence(cb: () => void) {
    this.presenceListeners.add(cb);
    return () => this.presenceListeners.delete(cb);
  }

  onStatus(cb: () => void) {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  private setStatus(s: Status) {
    if (s === this.status) return;
    this.status = s;
    this.listeners.forEach((l) => l());
  }

  private send(msg: unknown) {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(msg));
  }

  private sendSubscribe(boardId: string) {
    const after = queryClient.getQueryData<BoardSnapshot>(boardKey(boardId))?.lastEventId;
    // Ohne geladenen Stand erst abonnieren, wenn der Snapshot da ist (BoardPage ruft erneut auf)
    if (after === undefined) return;
    this.send({ type: 'subscribe', boardId, after });
    // Nach (Neu-)Abonnieren die offene Karte erneut melden
    const viewing = this.viewing.get(boardId);
    if (viewing !== undefined) this.send({ type: 'presence', boardId, cardId: viewing });
  }

  resubscribe(boardId: string) {
    this.sendSubscribe(boardId);
  }

  private wake = () => {
    if (document.visibilityState !== 'visible') return;
    if (!this.ws || this.ws.readyState > WebSocket.OPEN) {
      clearTimeout(this.timer);
      this.attempt = 0;
      this.connect();
    }
  };

  private connect() {
    if (!this.active) return;
    this.setStatus('connecting');
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    const ws = new WebSocket(`${proto}://${location.host}/api/ws`);
    this.ws = ws;
    ws.onopen = () => {
      this.attempt = 0;
      this.setStatus('online');
      for (const id of this.boards.keys()) this.sendSubscribe(id);
      if (this.cardWatchers > 0) {
        this.send({ type: 'watch-cards', on: true });
        // Änderungen während der Pause nachholen
        void queryClient.invalidateQueries({ queryKey: ['dash-data'] });
      }
      clearInterval(this.ping);
      this.ping = setInterval(() => this.send({ type: 'ping' }), 25_000);
      // Benachrichtigungen könnten während der Pause gekommen sein
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
      for (const set of this.collab.values()) for (const ch of set) ch.open();
    };
    ws.onmessage = (m) => {
      let msg: ServerMessage;
      try {
        msg = JSON.parse(m.data);
      } catch {
        return;
      }
      if (msg.type.startsWith('y-') && 'cardId' in msg) this.collab.get(msg.cardId)?.forEach((ch) => ch.message(msg as Extract<ServerMessage, { cardId: string }>));
      else if (msg.type === 'event') applyEvent(queryClient, msg.event);
      else if (msg.type === 'resync') void queryClient.invalidateQueries({ queryKey: boardKey(msg.boardId) });
      else if (msg.type === 'notification') onNotification(msg.notification);
      else if (msg.type === 'structure') { refreshStructure(); void queryClient.invalidateQueries({queryKey:['planning']}); }
      else if (msg.type === 'cards-changed') {
        void queryClient.invalidateQueries({ queryKey: ['dash-data'] });
        void queryClient.invalidateQueries({ queryKey: ['planning'] });
      }
      else if (msg.type === 'presence') {
        this.presence.set(msg.boardId, msg.users);
        this.presenceListeners.forEach((l) => l());
      }
    };
    ws.onclose = () => {
      clearInterval(this.ping);
      if (this.ws === ws) for (const set of this.collab.values()) for (const ch of set) ch.close();
      // Ohne Verbindung wissen wir nicht, wer da ist
      if (this.presence.size) {
        this.presence.clear();
        this.presenceListeners.forEach((l) => l());
      }
      if (this.ws !== ws || !this.active) return;
      this.setStatus('offline');
      const delay = Math.min(30_000, 1000 * 2 ** this.attempt++) + Math.random() * 500;
      this.timer = setTimeout(() => this.connect(), delay);
    };
  }
}

export const NOTIFICATION_TEXT: Record<Notification['type'], string> = {
  assigned: 'hat dir eine Karte zugewiesen',
  mentioned: 'hat dich erwähnt',
  comment: 'hat kommentiert',
  invited: 'hat dich zu einem Board hinzugefügt',
  automation: 'Hinweis',
  due: 'Frist läuft in weniger als 24 Stunden ab',
  reminder: 'Erinnerung',
  handover: 'Kann beginnen',
  form: 'Neue Einsendung',
};

function onNotification(n: Notification) {
  queryClient.setQueryData<{ notifications: Notification[]; unread: number }>(['notifications'], (d) =>
    d ? { notifications: [n, ...d.notifications], unread: d.unread + 1 } : d,
  );
  if (n.type === 'invited') void queryClient.invalidateQueries({ queryKey: ['boards'] });
  if (n.type === 'automation') {
    toast.info(`${n.text ?? 'Hinweis'}${n.cardTitle ? ` („${n.cardTitle}“)` : ''}`);
    return;
  }
  const who = n.type === 'due' ? '' : `${n.actorName ?? 'Jemand'} `;
  toast.info(`${who}${NOTIFICATION_TEXT[n.type]}${n.cardTitle ? `: „${n.cardTitle}“` : ''}`);
}

const EMPTY: PresenceEntry[] = [];

export const realtime = new Realtime();

/** Wer ist gerade im Board (inkl. mir), und wer hat welche Karte offen */
export function usePresence(boardId: string): PresenceEntry[] {
  return useSyncExternalStore(
    (cb) => realtime.onPresence(cb),
    () => realtime.getPresence(boardId),
  );
}

/** Meldet die offene Karte (null = keine) für die Präsenz */
export function useViewingCard(boardId: string, cardId: string | null) {
  useEffect(() => {
    realtime.setViewing(boardId, cardId);
  }, [boardId, cardId]);
}

export function useRealtimeConnection(loggedIn: boolean) {
  useEffect(() => {
    if (!loggedIn) return;
    realtime.start();
    return () => realtime.stop();
  }, [loggedIn]);
}

export function useBoardRealtime(boardId: string, ready: boolean) {
  useEffect(() => realtime.subscribe(boardId), [boardId]);
  useEffect(() => {
    if (ready) realtime.resubscribe(boardId);
  }, [boardId, ready]);
}

export function useConnectionStatus(): Status {
  return useSyncExternalStore(
    (cb) => realtime.onStatus(cb),
    () => realtime.status,
  );
}
